"use client";

import type { LeadFlowAnswerFieldName, LeadFlowAnswers } from "../types";

import { useCallback, useSyncExternalStore } from "react";
import { useLeadFlowAnswerStore } from "../components/LeadFlowAnswerStoreProvider";

/** Every answer on the page, re-rendering on any change. */
function useAnswers(): LeadFlowAnswers {
  const store = useLeadFlowAnswerStore();

  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot,
  );
}

/** One field's answer and its setter, re-rendering only when it changes. */
function useAnswer<TFieldName extends LeadFlowAnswerFieldName>(
  fieldName: TFieldName,
): [LeadFlowAnswers[TFieldName], (value: LeadFlowAnswers[TFieldName]) => void] {
  const store = useLeadFlowAnswerStore();

  const value = useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot()[fieldName],
    () => store.getServerSnapshot()[fieldName],
  );

  const setValue = useCallback(
    (nextValue: LeadFlowAnswers[TFieldName]) => {
      store.setAnswer(fieldName, nextValue);
    },
    [fieldName, store],
  );

  return [value, setValue];
}

/**
 * Whether the page's stored answers have been read. Anything that treats a
 * missing answer as "not given" (for example the deep-link guard) must wait
 * for this, because the snapshot is empty until hydration finishes.
 */
function useAnswersLoaded(): boolean {
  const store = useLeadFlowAnswerStore();

  return useSyncExternalStore(
    store.subscribe,
    store.hasLoadedAnswers,
    () => false,
  );
}

export { useAnswer, useAnswers, useAnswersLoaded };
